import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

// Servicio de traducción para la app
@Injectable({ providedIn: 'root' })
export class LanguageService {
  // Diccionario de traducciones cargadas
  private translations: { [key: string]: any } = {};
  // Idioma actual
  private static currentLang = 'es'; // Idioma por defecto
  // Observable para cambios de idioma
  private languageChanged = new BehaviorSubject<string>(LanguageService.currentLang);
  public languageChanged$ = this.languageChanged.asObservable();

  constructor(private http: HttpClient) {
    // Cargar idioma por defecto al inicializar el servicio
    this.initializeLanguage();
  }

  // Inicializa el idioma por defecto
  private async initializeLanguage(): Promise<void> {
    // Obtener idioma guardado en localStorage o usar el por defecto
    const savedLang = localStorage.getItem('selectedLanguage') || LanguageService.currentLang;
    await this.loadLanguage(savedLang);
  }

  // Carga el archivo de idioma correspondiente
  async loadLanguage(lang: string): Promise<void> {
    LanguageService.currentLang = lang;
    const path = `assets/i18n/${lang}.json`;

    try {
      // Carga las traducciones desde el archivo JSON
      console.log(`Cargando traducciones desde: ${path}`);
      this.translations = await firstValueFrom(this.http.get<{ [key: string]: any }>(path)) || {};
      
      // Guardar idioma seleccionado
      localStorage.setItem('selectedLanguage', lang);
      
      // Notificar cambio de idioma
      this.languageChanged.next(lang);
    } catch (err) {
      console.error(`Error al cargar traducciones para ${lang}:`, err);
      // Si falla, deja el diccionario vacío
      this.translations = {};
    }
  }

  // Traduce una clave, reemplazando parámetros si se proporcionan
  translate(key: string, params?: { [key: string]: string }): string {
    // Buscar la traducción usando notación de punto (ej: "login.email")
    let translation = this.getNestedTranslation(key);
    
    // Si no se encuentra, usar la clave como fallback
    if (translation === null) {
      translation = key;
    }

    // Reemplazar parámetros si se proporcionan
    if (params && typeof translation === 'string') {
      for (const paramKey of Object.keys(params)) {
        const paramValue = params[paramKey];
        translation = translation.replace(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), paramValue);
      }
    }

    return translation;
  }

  // Obtiene una traducción anidada usando notación de punto
  private getNestedTranslation(key: string): string | null {
    const keys = key.split('.');
    let current: any = this.translations;

    for (const k of keys) {
      if (current && typeof current === 'object' && current.hasOwnProperty(k)) {
        current = current[k];
      } else {
        return null;
      }
    }

    return typeof current === 'string' ? current : null;
  }

  // Devuelve el idioma actual
  static getCurrentLanguage(): string {
    return LanguageService.currentLang;
  }

  // Cambia el idioma de la aplicación
  async changeLanguage(lang: string): Promise<void> {
    if (lang !== LanguageService.currentLang) {
      await this.loadLanguage(lang);
    }
  }

  // Obtiene la lista de idiomas disponibles
  getAvailableLanguages(): { code: string; name: string }[] {
    return [
      { code: 'es', name: 'Español' },
      { code: 'pt', name: 'Português' }
    ];
  }

  // Verifica si existe una traducción para la clave dada
  hasTranslation(key: string): boolean {
    return this.getNestedTranslation(key) !== null;
  }
}
